// src/components/EditItem.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../api/client";

const EditItem = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialItem = location.state?.item || null;

  const [itemData, setItemData] = useState(initialItem);
  const [loading, setLoading] = useState(!initialItem);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    category: "product",
    description: "",
    price: "",
    stockQty: "",
    sold: false,
    imageFile: null,
    videoUrl: "", // ✅ added video URL field
  });

  // Load item if not passed via navigation state
  useEffect(() => {
    let ignore = false;

    async function load() {
      if (initialItem) {
        setFormData({
          name: initialItem.name || "",
          category: initialItem.category || "product",
          description: initialItem.description || "",
          price: initialItem.price ?? "",
          stockQty: initialItem.stockQty ?? "",
          sold: !!initialItem.sold,
          imageFile: null,
          videoUrl: initialItem.videoUrl || "", // ✅ populate existing video URL
        });
        return;
      }

      try {
        const res = await api.get(`/api/items/${id}`);
        if (ignore) return;
        const data = res.data;
        setItemData(data);
        setFormData({
          name: data.name || "",
          category: data.category || "product",
          description: data.description || "",
          price: data.price ?? "",
          stockQty: data.stockQty ?? "",
          sold: !!data.sold,
          imageFile: null,
          videoUrl: data.videoUrl || "", // ✅ populate existing video URL
        });
        setLoading(false);
      } catch (err) {
        if (ignore) return;
        setError(err.response?.data?.error || err.message);
        setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [id, initialItem]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "file") {
      setFormData((prev) => ({ ...prev, imageFile: files?.[0] || null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = new FormData();
    payload.append("name", formData.name);
    payload.append("category", formData.category);
    payload.append("description", formData.description);
    payload.append("price", formData.price);
    if (formData.stockQty !== "") payload.append("stockQty", formData.stockQty);
    payload.append("sold", String(formData.sold));
    if (formData.imageFile) payload.append("image", formData.imageFile);
    if (formData.videoUrl) payload.append("videoUrl", formData.videoUrl); // ✅ append video URL

    try {
      await api.put(`/api/items/${id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Item updated successfully!");
      navigate("/admin-products");
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to update item";
      alert("Error updating item: " + msg);
    }
  };

  if (loading) return <div style={{ padding: 20 }}>Loading item data...</div>;
  if (error) return <div style={{ padding: 20, color: "red" }}>Error: {error}</div>;

  return (
    <div style={{ padding: 20 }}>
      <h2>Edit Item</h2>

      {itemData?.imageUrl ? (
        <div style={{ marginBottom: 12 }}>
          <img
            src={itemData.imageUrl}
            alt={itemData.name}
            style={{ width: 160, height: 160, objectFit: "cover", borderRadius: 8 }}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        </div>
      ) : null}

      <form onSubmit={handleSubmit} style={{ maxWidth: 520 }}>
        <label>
          Name:
          <input
            type="text"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            style={{ width: "100%", marginBottom: 10 }}
          />
        </label>

        <label>
          Category:
          <select
            name="category"
            required
            value={formData.category}
            onChange={handleChange}
            style={{ width: "100%", marginBottom: 10 }}
          >
            <option value="product">Product</option>
            <option value="livestock">Livestock</option>
          </select>
        </label>

        <label>
          Description:
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            style={{ width: "100%", marginBottom: 10 }}
          />
        </label>

        <label>
          Price:
          <input
            type="number"
            name="price"
            required
            min="0"
            step="0.01"
            value={formData.price}
            onChange={handleChange}
            style={{ width: "100%", marginBottom: 10 }}
          />
        </label>

        <label>
          Stock Qty:
          <input
            type="number"
            name="stockQty"
            min="0"
            step="1"
            value={formData.stockQty}
            onChange={handleChange}
            style={{ width: "100%", marginBottom: 10 }}
          />
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span>Sold:</span>
          <input type="checkbox" name="sold" checked={formData.sold} onChange={handleChange} />
        </label>

        <label>
          Upload New Image:
          <input
            type="file"
            name="image"
            accept="image/*"
            onChange={handleChange}
            style={{ display: "block", marginTop: 10, marginBottom: 10 }}
          />
        </label>

        <label>
          YouTube Video URL:
          <input
            type="url"
            name="videoUrl"
            placeholder="https://www.youtube.com/watch?v=..."
            value={formData.videoUrl}
            onChange={handleChange}
            style={{ width: "100%", marginBottom: 10 }}
          />
        </label>

        <button type="submit" style={{ padding: "8px 16px" }}>
          Update Item
        </button>
      </form>
    </div>
  );
};

export default EditItem;
